import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, Check } from "lucide-react";
import { IFilterSet } from "../types";

interface FilterGroupProps {
  filterSet: IFilterSet;
  selectedValues: string[];
  onSelectFilter: (value: string) => void;
  onRangeChange: (min: string, max: string) => void;
}

const MultipleChoiceFilter: React.FC<{
  filterSet: IFilterSet;
  selected: string[];
  onSelect: (value: string) => void;
}> = ({ filterSet, selected, onSelect }) => {
  return (
    <div className="srb-flex srb-flex-wrap srb-gap-2">
      {filterSet.filterItems.map((item) => {
        const isSelected = selected.includes(item.value);
        return (
          <button
            key={String(item.id)}
            onClick={() => onSelect(item.value)}
            className={`srb-filter-chip srb-inline-flex srb-items-center srb-gap-1.5 srb-px-3 srb-py-2 srb-text-sm srb-font-medium srb-cursor-pointer srb-transition-all srb-bg-background srb-rounded-md ${
              isSelected
                ? "srb-filter-chip-selected srb-text-primary"
                : "srb-filter-chip-unselected srb-text-main"
            }`}
          >
            {isSelected && <Check size={14} strokeWidth={2.5} />}
            {item.value}
          </button>
        );
      })}
    </div>
  );
};

const BooleanChoiceFilter: React.FC<{
  filterSet: IFilterSet;
  isSelected: boolean;
  onToggle: () => void;
}> = ({ filterSet, isSelected, onToggle }) => {
  return (
    <label className="srb-flex srb-items-center srb-gap-2 srb-cursor-pointer srb-text-sm srb-text-main">
      <input
        type="checkbox"
        checked={isSelected}
        onChange={onToggle}
        className="srb-w-4 srb-h-4 srb-cursor-pointer"
        style={{ accentColor: "var(--srb-primary-color)" }}
      />
      {filterSet.title}
    </label>
  );
};

const RangeFilter: React.FC<{
  filterSet: IFilterSet;
  selected: string[];
  onRangeChange: (min: string, max: string) => void;
}> = ({ filterSet, selected, onRangeChange }) => {
  const minItem = filterSet.filterItems.find((item) => item.id === "min");
  const maxItem = filterSet.filterItems.find((item) => item.id === "max");
  const absMin = Math.floor(Number(minItem?.value || 0));
  const absMax = Math.ceil(Number(maxItem?.value || 10000));

  const [localMin, setLocalMin] = useState(
    selected[0] ? Number(selected[0]) : absMin,
  );
  const [localMax, setLocalMax] = useState(
    selected[1] ? Number(selected[1]) : absMax,
  );

  useEffect(() => {
    setLocalMin(selected[0] ? Number(selected[0]) : absMin);
    setLocalMax(selected[1] ? Number(selected[1]) : absMax);
  }, [selected, absMin, absMax]);

  const isPriceRange = filterSet.param === "price_range";

  return (
    <div>
      <div className="srb-flex srb-items-center srb-gap-3 srb-mb-3">
        <div className="srb-flex-1">
          <label className="srb-block srb-text-xs srb-text-muted srb-mb-1">
            Min
          </label>
          <div className="srb-relative">
            {isPriceRange && (
              <span className="srb-absolute srb-left-2 srb-top-1/2 srb--translate-y-1/2 srb-text-muted srb-text-sm">
                $
              </span>
            )}
            <input
              type="number"
              min={absMin}
              max={absMax}
              value={localMin}
              onChange={(e) => {
                const val = Number(e.target.value);
                setLocalMin(Math.max(absMin, Math.min(val, localMax)));
              }}
              className={`srb-range-input srb-w-full srb-py-2 srb-bg-background srb-text-main srb-text-sm srb-rounded-md focus:srb-outline-none ${isPriceRange ? "srb-pl-6 srb-pr-2" : "srb-px-3"}`}
            />
          </div>
        </div>
        <span className="srb-text-muted srb-pt-4">-</span>
        <div className="srb-flex-1">
          <label className="srb-block srb-text-xs srb-text-muted srb-mb-1">
            Max
          </label>
          <div className="srb-relative">
            {isPriceRange && (
              <span className="srb-absolute srb-left-2 srb-top-1/2 srb--translate-y-1/2 srb-text-muted srb-text-sm">
                $
              </span>
            )}
            <input
              type="number"
              min={absMin}
              max={absMax}
              value={localMax}
              onChange={(e) => {
                const val = Number(e.target.value);
                setLocalMax(Math.min(absMax, Math.max(val, localMin)));
              }}
              className={`srb-range-input srb-w-full srb-py-2 srb-bg-background srb-text-main srb-text-sm srb-rounded-md focus:srb-outline-none ${isPriceRange ? "srb-pl-6 srb-pr-2" : "srb-px-3"}`}
            />
          </div>
        </div>
      </div>

      {/* Dual range sliders */}
      <div className="srb-mb-3">
        <div className="srb-relative srb-pt-1">
          <input
            type="range"
            min={absMin}
            max={absMax}
            step={isPriceRange ? 10 : 1}
            value={localMin}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (val <= localMax) setLocalMin(val);
            }}
            className="srb-w-full srb-h-2 srb-appearance-none srb-cursor-pointer srb-outline-none"
            style={{
              background: `linear-gradient(to right, var(--srb-secondary-color) 0%, var(--srb-secondary-color) ${((localMin - absMin) / (absMax - absMin)) * 100}%, var(--srb-primary-color) ${((localMin - absMin) / (absMax - absMin)) * 100}%, var(--srb-primary-color) ${((localMax - absMin) / (absMax - absMin)) * 100}%, var(--srb-secondary-color) ${((localMax - absMin) / (absMax - absMin)) * 100}%, var(--srb-secondary-color) 100%)`,
            }}
          />
        </div>
        <div className="srb-relative srb-pt-1">
          <input
            type="range"
            min={absMin}
            max={absMax}
            step={isPriceRange ? 10 : 1}
            value={localMax}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (val >= localMin) setLocalMax(val);
            }}
            className="srb-w-full srb-h-2 srb-appearance-none srb-cursor-pointer srb-outline-none"
          />
        </div>
      </div>

      <button
        onClick={() => onRangeChange(localMin.toString(), localMax.toString())}
        className="srb-apply-btn srb-w-full srb-px-3 srb-py-2 srb-bg-primary srb-text-white srb-text-sm srb-font-semibold hover:srb-opacity-90 srb-transition-all srb-cursor-pointer srb-rounded-md"
      >
        Apply
      </button>
    </div>
  );
};

export const FilterGroup: React.FC<FilterGroupProps> = ({
  filterSet,
  selectedValues,
  onSelectFilter,
  onRangeChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const isRangeFilter =
    filterSet.type === "single_choice" &&
    filterSet.filterItems.some(
      (item) => item.id === "min" || item.id === "max",
    );

  const selectedCount = isRangeFilter
    ? selectedValues.length === 2
      ? 1
      : 0
    : selectedValues.length;

  return (
    <div className="srb-filter-group srb-border-b srb-border-subtle">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="srb-filter-header srb-w-full srb-flex srb-items-center srb-justify-between srb-py-3 srb-px-2 srb-bg-background srb-cursor-pointer srb-text-main srb-rounded-md hover:srb-bg-hover srb-transition-all"
      >
        <span className="srb-text-sm srb-font-semibold srb-capitalize">
          {filterSet.title}
          {selectedCount > 0 && (
            <span className="srb-ml-1 srb-text-xs srb-text-primary">
              ({selectedCount})
            </span>
          )}
        </span>
        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>

      {isExpanded && (
        <div className="srb-pb-4 srb-px-2 srb-pt-1">
          {filterSet.type === "boolean_choice" ? (
            <BooleanChoiceFilter
              filterSet={filterSet}
              isSelected={selectedValues.includes("true")}
              onToggle={() => onSelectFilter("true")}
            />
          ) : isRangeFilter ? (
            <RangeFilter
              filterSet={filterSet}
              selected={selectedValues}
              onRangeChange={onRangeChange}
            />
          ) : (
            <MultipleChoiceFilter
              filterSet={filterSet}
              selected={selectedValues}
              onSelect={onSelectFilter}
            />
          )}
        </div>
      )}
    </div>
  );
};
